import { api } from "./client";
import type {
  ApiEnvelope,
  PageQuery,
  PageResponse,
  User,
  Doctor,
  Post,
  PostStatusFilter,
  Product,
  UserRole,
  UserStatus,
  DoctorRevenue,
  PlatformRevenue,
  Payment,
  PaymentSummary,
  Order,
  Withdrawal,
  Appointment,
  Specialization,
  ProductCategory,
  Medicine,
  LabTest,
  AppSetting,
  PushResult,
  PushLog,
  UserStats,
  SubscriptionPlan,
  AuditEntry,
  AuditDetail,
  PermissionCatalog,
} from "./types";

export { api } from "./client";
export { authApi } from "./auth";
export * from "./types";

// Normalise the {q, page, size} bag → axios params, dropping empties
// so the server sees `?q=&page=0&size=6` only when we actually mean it.
function pageParams(query: PageQuery & Record<string, unknown> = {}): Record<string, unknown> {
  const params: Record<string, unknown> = { ...query };
  if (params.q === "" || params.q === undefined) delete params.q;
  return params;
}

// Helper to cast typed page queries into the index-signature param bag.
function p<T extends PageQuery>(query: T = {} as T) {
  return pageParams(query as PageQuery & Record<string, unknown>);
}

export const usersApi = {
  list: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<User>>>("/api/v1/users", { params: p(query) }),
  get: (id: string) => api.get<ApiEnvelope<User>>(`/api/v1/users/${id}`),
  create: (body: { name: string; email: string; phoneNumber?: string; role: UserRole }) =>
    api.post<ApiEnvelope<User>>("/api/v1/users", body),
  update: (
    id: string,
    body: Partial<{ name: string; photoUrl: string; address: string; gender: string; bio: string }>,
  ) => api.put<ApiEnvelope<User>>(`/api/v1/users/${id}`, body),
  setStatus: (id: string, status: UserStatus) =>
    api.put<ApiEnvelope<User>>(`/api/v1/users/${id}/status`, { status }),
  changeRole: (id: string, role: UserRole) =>
    api.put<ApiEnvelope<User>>(`/api/v1/users/${id}/role`, { role }),
  stats: () => api.get<ApiEnvelope<UserStats>>("/api/v1/users/stats"),
  /** Ids of every ACTIVE user with a role — used to target a push at a role. */
  idsByRole: (role: UserRole) => api.get<ApiEnvelope<string[]>>("/api/v1/users/ids", { params: { role } }),
};

export const doctorsApi = {
  listPending: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<Doctor>>>("/api/v1/doctors/pending", { params: p(query) }),
  list: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<Doctor>>>("/api/v1/doctors", { params: p(query) }),
  get: (id: string) => api.get<ApiEnvelope<Doctor>>(`/api/v1/doctors/${id}`),
  verify: (id: string) => api.post<ApiEnvelope<Doctor>>(`/api/v1/doctors/${id}/verify`),
  reject: (id: string, reason: string) =>
    api.post<ApiEnvelope<Doctor>>(`/api/v1/doctors/${id}/reject`, { reason }),
};

export const postsApi = {
  list: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<Post>>>("/api/v1/posts", { params: p(query) }),
  /** Admin view — every post regardless of draft/taken-down state. */
  adminList: (query: PageQuery & { status?: PostStatusFilter } = {}) =>
    api.get<ApiEnvelope<PageResponse<Post>>>("/api/v1/posts/admin", { params: p(query) }),
  get: (id: string) => api.get<ApiEnvelope<Post>>(`/api/v1/posts/${id}`),
  update: (
    id: string,
    body: Partial<{ title: string; content: string; category: string; isPremium: boolean; isDraft: boolean }>,
  ) => api.put<ApiEnvelope<Post>>(`/api/v1/posts/${id}`, body),
  takedown: (id: string, reason?: string) =>
    api.post<ApiEnvelope<Post>>(`/api/v1/posts/${id}/takedown`, { reason }),
  restore: (id: string) => api.post<ApiEnvelope<Post>>(`/api/v1/posts/${id}/restore`),
  remove: (id: string) => api.delete<ApiEnvelope<null>>(`/api/v1/posts/${id}`),
};

export interface ProductBody {
  name: string;
  description?: string;
  price: number;
  imageUrl?: string;
  stock?: number;
  sellerId?: string;
  isActive?: boolean;
}

export const productsApi = {
  list: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<Product>>>("/api/v1/products", { params: p(query) }),
  get: (id: string) => api.get<ApiEnvelope<Product>>(`/api/v1/products/${id}`),
  create: (body: ProductBody) => api.post<ApiEnvelope<Product>>("/api/v1/products", body),
  update: (id: string, body: Partial<ProductBody>) =>
    api.put<ApiEnvelope<Product>>(`/api/v1/products/${id}`, body),
  remove: (id: string) => api.delete<ApiEnvelope<null>>(`/api/v1/products/${id}`),
};

// ── Finance & reporting (wallet / payment / order services) ──

export const revenueApi = {
  platform: () => api.get<ApiEnvelope<PlatformRevenue>>("/api/v1/revenue/platform"),
  doctors: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<DoctorRevenue>>>("/api/v1/revenue/doctors", { params: p(query) }),
  doctor: (doctorId: string) =>
    api.get<ApiEnvelope<DoctorRevenue>>(`/api/v1/revenue/doctors/${doctorId}`),
  topDoctors: (limit = 8) =>
    api.get<ApiEnvelope<DoctorRevenue[]>>("/api/v1/revenue/top-doctors", { params: { limit } }),
};

export const withdrawalsApi = {
  adminAll: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<Withdrawal>>>("/api/v1/withdrawals/admin/all", { params: p(query) }),
  pendingCount: () =>
    api.get<ApiEnvelope<number>>("/api/v1/withdrawals/admin/pending-count"),
  process: (id: string, body: { status: string; adminNotes?: string; transactionReference?: string }) =>
    api.post<ApiEnvelope<Withdrawal>>(`/api/v1/withdrawals/admin/${id}/process`, body),
};

export const paymentsApi = {
  adminAll: (query: PageQuery & { status?: string; userId?: string } = {}) =>
    api.get<ApiEnvelope<PageResponse<Payment>>>("/api/v1/payments/admin/all", { params: p(query) }),
  summary: () => api.get<ApiEnvelope<PaymentSummary>>("/api/v1/payments/admin/summary"),
};

export const ordersApi = {
  adminAll: (query: PageQuery & { status?: string } = {}) =>
    api.get<ApiEnvelope<PageResponse<Order>>>("/api/v1/orders/admin/all", { params: p(query) }),
  /** Admin may move an order along its lifecycle; the server enforces valid steps. */
  setStatus: (id: string, status: string) =>
    api.put<ApiEnvelope<Order>>(`/api/v1/orders/${id}/status`, { status }),
};

// ---- Catalog reference data -------------------------------------------------
// Specializations and categories come back as plain lists (small); medicines
// and lab tests are paged and searched with `query` (not `q`).

type Body<T> = Partial<Omit<T, "id">>;

export const specializationsApi = {
  list: () => api.get<ApiEnvelope<Specialization[]>>("/api/v1/specializations"),
  create: (body: Body<Specialization>) => api.post<ApiEnvelope<Specialization>>("/api/v1/specializations", body),
  update: (id: string, body: Body<Specialization>) =>
    api.put<ApiEnvelope<Specialization>>(`/api/v1/specializations/${id}`, body),
};

export const categoriesApi = {
  list: () => api.get<ApiEnvelope<ProductCategory[]>>("/api/v1/product-categories"),
  create: (body: Body<ProductCategory>) => api.post<ApiEnvelope<ProductCategory>>("/api/v1/product-categories", body),
  update: (id: string, body: Body<ProductCategory>) =>
    api.put<ApiEnvelope<ProductCategory>>(`/api/v1/product-categories/${id}`, body),
  remove: (id: string) => api.delete<ApiEnvelope<null>>(`/api/v1/product-categories/${id}`),
};

export const medicinesApi = {
  list: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<Medicine>>>("/api/v1/medicines", {
      params: { page: query.page, size: query.size, ...(query.q ? { query: query.q } : {}) },
    }),
  create: (body: Body<Medicine>) => api.post<ApiEnvelope<Medicine>>("/api/v1/medicines", body),
  update: (id: string, body: Body<Medicine>) => api.put<ApiEnvelope<Medicine>>(`/api/v1/medicines/${id}`, body),
  remove: (id: string) => api.delete<ApiEnvelope<null>>(`/api/v1/medicines/${id}`),
};

export const labTestsApi = {
  list: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<LabTest>>>("/api/v1/lab-tests", {
      params: { page: query.page, size: query.size, ...(query.q ? { query: query.q } : {}) },
    }),
  create: (body: Body<LabTest>) => api.post<ApiEnvelope<LabTest>>("/api/v1/lab-tests", body),
  update: (id: string, body: Body<LabTest>) => api.put<ApiEnvelope<LabTest>>(`/api/v1/lab-tests/${id}`, body),
  remove: (id: string) => api.delete<ApiEnvelope<null>>(`/api/v1/lab-tests/${id}`),
};

export const appointmentsApi = {
  adminAll: (query: PageQuery & { status?: string } = {}) =>
    api.get<ApiEnvelope<PageResponse<Appointment>>>("/api/v1/appointments/admin/all", { params: p(query) }),
  setStatus: (id: string, status: string, doctorResponse?: string) =>
    api.put<ApiEnvelope<Appointment>>(`/api/v1/appointments/admin/${id}/status`, { status, doctorResponse }),
};

export const profileApi = {
  me: () => api.get<ApiEnvelope<User>>("/api/v1/users/me"),
  update: (
    body: Partial<{ name: string; photoUrl: string; address: string; gender: string; bio: string }>,
  ) => api.put<ApiEnvelope<User>>("/api/v1/users/me", body),
};

// ---- Platform -----------------------------------------------------------------

export const settingsApi = {
  /** Every setting, public or not (admin). */
  all: () => api.get<ApiEnvelope<AppSetting[]>>("/api/v1/settings/all"),
  /** Upserts the given keys; returns every setting afterwards. */
  save: (values: Record<string, string>) => api.put<ApiEnvelope<AppSetting[]>>("/api/v1/settings", values),
};

export const pushApi = {
  send: (body: { userId?: string; userIds?: string[]; title: string; body: string; deepLink?: string; type?: string }) =>
    api.post<ApiEnvelope<PushResult>>("/api/v1/push", body),
  history: (query: PageQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<PushLog>>>("/api/v1/push/history", { params: { page: query.page, size: query.size } }),
  deviceCount: () => api.get<ApiEnvelope<{ count: number }>>("/api/v1/devices/count"),
};

export const plansApi = {
  /** Every plan including inactive ones (admin); the app only sees active ones. */
  all: () => api.get<ApiEnvelope<SubscriptionPlan[]>>("/api/v1/subscriptions/plans/all"),
  create: (body: Partial<Omit<SubscriptionPlan, "id">>) =>
    api.post<ApiEnvelope<SubscriptionPlan>>("/api/v1/subscriptions/plans", body),
  update: (id: string, body: Partial<Omit<SubscriptionPlan, "id">>) =>
    api.put<ApiEnvelope<SubscriptionPlan>>(`/api/v1/subscriptions/plans/${id}`, body),
  remove: (id: string) => api.delete<ApiEnvelope<null>>(`/api/v1/subscriptions/plans/${id}`),
  toggleActive: (id: string) => api.post<ApiEnvelope<SubscriptionPlan>>(`/api/v1/subscriptions/plans/${id}/toggle-active`),
};

export interface AuditQuery extends PageQuery {
  service?: string;
  method?: string;
  identity?: string;
  status?: string;
  from?: string;
  to?: string;
}

export const auditApi = {
  list: (query: AuditQuery = {}) =>
    api.get<ApiEnvelope<PageResponse<AuditEntry>>>("/api/v1/audit", { params: p(query) }),
  get: (id: string) => api.get<ApiEnvelope<AuditDetail>>(`/api/v1/audit/${id}`),
  services: () => api.get<ApiEnvelope<string[]>>("/api/v1/audit/services"),
};

export const permissionsApi = {
  catalog: () => api.get<ApiEnvelope<PermissionCatalog>>("/api/v1/config/permissions"),
};
